import { createSearchRegexFor } from "../util/string";


/**
 * Helper class for searching through a list of KnockoutDataProvider results.
 */
export class KnockoutDataSearchInfo {

    constructor(rowIdIndex: number|string, exactMatchIgnoresCase: boolean) {
        this._rowIdIndex = rowIdIndex;
        this._exactMatchIgnoresCase = !!exactMatchIgnoresCase;
    }

    /**
     * Searches for the next match of the specified regular expression in data source starting at the specified index.
     *
     * @param regex
     *     Expression to match.
     * @param startingIndex
     *     Data index to begin searching.
     * @returns
     *     Index of the next match.
     */
    findNextMatch(regex: RegExp, startingIndex: number): number {
        const items = (this._data && this._data.items);
        if (items) {
            const rowIdIndex = this._rowIdIndex;
            const l = items.length;

            for (let i = startingIndex; i < l; i += 1) {
                if (items[i][rowIdIndex].match(regex)) {
                    return i;
                }
            }
        }

        return -1;
    }

    /**
     * Returns row that uniquely matched search value last time search was run.
     *
     * @param defaultValue
     *     Value to return if the search produced no results.
     * @returns
     *     Row that uniquely matched last search value; otherwise, default value.
     */
    getFoundRow(defaultValue?: any): any {
        return (this._firstIndex !== -1 && this._nextIndex === -1
            ? this._data.items[this._firstIndex]
            : defaultValue);
    }

    /**
     * Resets data source and invalidate result of last search.
     */
    reset(): void {
        this._firstIndex = -1;
        this._nextIndex = -1;
        this._exactMatch = false;
        this._sourceValue = null;
    }

    /**
     * Searches for the specified value.
     *
     * @param value
     *     Value to search for.
     * @returns
     *     "value" if data source is not available or searching for "null" value;
     *     row ID if unique match is found; false otherwise.
     */
    search(value: string): (string|number|boolean) {
        // does it make sense to search?
        if (value === null || !this._data) {
            this.reset();
            this._exactMatch = true;
            return value;
        }

        // are we searching for the same value as last time?
        const foundRow = this.getFoundRow(false);
        const foundRowId = (foundRow && foundRow[this._rowIdIndex]);
        if (this._sourceValue === value) {
            return foundRowId;
        }
        if (foundRowId === value) {
            this._sourceValue = value;
            this._exactMatch = true;
            return foundRowId;
        }

        // remember this search
        this.reset();
        this._sourceValue = value;
        if (typeof value !== "string") {
            return false;
        }

        // try to find an exact match
        this._firstIndex = this._findExactMatch(value);
        if (this._firstIndex !== -1) {
            this._exactMatch = true;
            return this.getFoundRow()[this._rowIdIndex];
        }

        // try to find a single non-exact match
        const regex = createSearchRegexFor(value);
        this._firstIndex = this.findNextMatch(regex, 0);
        if (this._firstIndex !== -1) {
            this._nextIndex = this.findNextMatch(regex, this._firstIndex + 1);
            if (this._nextIndex === -1) {
                return this.getFoundRow()[this._rowIdIndex];
            }
        }

        // either none or multiple (> 1) non-exact matches found
        return false;
    }

    get data(): any { return this._data; }
    set data(value: any) { this._data = value; }
    get exactMatch(): boolean { return this._exactMatch; }
    get nextIndex(): number { return this._nextIndex; }

    /**
     * Search for an exact match of the specified value in data source.
     *
     * @param value
     *     Value to search for.
     * @returns
     *     Index of match, or -1 if not found.
     */
    private _findExactMatch(value: string): number {
        const rows = (this._data && this._data.items);
        if (rows) {
            const rowIdIndex = this._rowIdIndex;
            const comparableTargetId = (this._exactMatchIgnoresCase ? value.toUpperCase() : value);
            const itemsLength = rows.length;
            for (let i = 0; i < itemsLength; i += 1) {
                const rowId = rows[i][rowIdIndex];
                if (comparableTargetId === (this._exactMatchIgnoresCase ? rowId.toUpperCase() : rowId)) {
                    return i;
                }
            }
        }

        return -1;
    }

    private _data: { items?: any[] };
    private _exactMatch: boolean;
    private _exactMatchIgnoresCase: boolean;
    private _firstIndex: number;
    private _nextIndex: number;
    private _rowIdIndex: number|string;
    private _sourceValue: string;

}



// WEBPACK FOOTER //
// /Users/johng/dev/web-pli/data-provider/_knockoutDataSearchInfo.ts